/**
 * Image processing utilities for upload
 */

// Max dimensions: 1024 on longer side, 768 on shorter side
const MAX_LONG_SIDE = 1024;
const MAX_SHORT_SIDE = 768;

// Preferred format: AVIF if supported, WebP as fallback
let preferredFormat: 'image/avif' | 'image/webp' | null = null;

/**
 * Check if browser supports AVIF encoding via canvas.toBlob
 * Caches result for performance
 */
async function getPreferredFormat(): Promise<'image/avif' | 'image/webp'> {
  if (preferredFormat) return preferredFormat;

  // Create a tiny test canvas
  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;

  // Try AVIF encoding
  const supportsAvif = await new Promise<boolean>((resolve) => {
    canvas.toBlob(
      (blob) => {
        // If blob is null or type is not AVIF, browser doesn't support it
        resolve(blob?.type === 'image/avif');
      },
      'image/avif',
      0.5
    );
  });

  preferredFormat = supportsAvif ? 'image/avif' : 'image/webp';
  console.log(`Image format: using ${preferredFormat}`);
  return preferredFormat;
}

/**
 * Get the file extension for the preferred format
 */
export async function getPreferredExtension(): Promise<'avif' | 'webp'> {
  const format = await getPreferredFormat();
  return format === 'image/avif' ? 'avif' : 'webp';
}

/**
 * Get quality setting based on format (AVIF needs lower quality for same result)
 */
function getQualityForFormat(format: string): number {
  return format === 'image/avif' ? 0.75 : 0.85;
}

export interface ImageDimensions {
  width: number;
  height: number;
}

/**
 * Calculate target dimensions respecting max resolution
 * Landscape: max 1024x768, Portrait: max 768x1024
 */
function calculateTargetDimensions(width: number, height: number): ImageDimensions {
  const isLandscape = width >= height;
  const maxWidth = isLandscape ? MAX_LONG_SIDE : MAX_SHORT_SIDE;
  const maxHeight = isLandscape ? MAX_SHORT_SIDE : MAX_LONG_SIDE;

  let targetWidth = width;
  let targetHeight = height;

  // Scale down if exceeds max dimensions while maintaining aspect ratio
  if (targetWidth > maxWidth) {
    targetHeight = (targetHeight * maxWidth) / targetWidth;
    targetWidth = maxWidth;
  }
  if (targetHeight > maxHeight) {
    targetWidth = (targetWidth * maxHeight) / targetHeight;
    targetHeight = maxHeight;
  }

  return { width: Math.round(targetWidth), height: Math.round(targetHeight) };
}

/**
 * Get dimensions of an image file
 */
export function getImageDimensions(file: File): Promise<ImageDimensions> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Convert image to optimal format (AVIF if supported, WebP fallback)
 * Max dimensions: 1024x768 for landscape, 768x1024 for portrait
 */
export async function convertToAVIF(
  file: File | Blob
): Promise<{ blob: Blob; width: number; height: number }> {
  const format = await getPreferredFormat();
  const quality = getQualityForFormat(format);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Calculate target dimensions based on orientation
      const { width, height } = calculateTargetDimensions(img.naturalWidth, img.naturalHeight);

      // Create canvas and draw image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to optimal format
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve({ blob, width, height });
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        format,
        quality
      );

      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Resize a blob to fit within max dimensions
 * Max dimensions: 1024x768 for landscape, 768x1024 for portrait
 */
export async function resizeImage(
  blob: Blob
): Promise<{ blob: Blob; width: number; height: number }> {
  const format = await getPreferredFormat();
  const quality = getQualityForFormat(format);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      // Calculate target dimensions based on orientation
      const { width, height } = calculateTargetDimensions(img.naturalWidth, img.naturalHeight);

      // If already within limits, return original
      if (width === img.naturalWidth && height === img.naturalHeight) {
        resolve({ blob, width, height });
        URL.revokeObjectURL(img.src);
        return;
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to optimal format
      canvas.toBlob(
        (resizedBlob) => {
          if (resizedBlob) {
            resolve({ blob: resizedBlob, width, height });
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        format,
        quality
      );

      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      reject(new Error('Failed to load image'));
      URL.revokeObjectURL(img.src);
    };
    img.src = URL.createObjectURL(blob);
  });
}

/**
 * Create cropped and resized image from canvas
 * Automatically resizes to max 1024x768 (landscape) or 768x1024 (portrait)
 */
export async function cropImage(
  imageSrc: string,
  crop: { x: number; y: number; width: number; height: number },
  rotation = 0
): Promise<{ blob: Blob; width: number; height: number }> {
  const format = await getPreferredFormat();
  const quality = getQualityForFormat(format);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      // First, create a canvas with the cropped area at original size
      const cropCanvas = document.createElement('canvas');
      const cropCtx = cropCanvas.getContext('2d');
      if (!cropCtx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Set canvas size to cropped area
      cropCanvas.width = crop.width;
      cropCanvas.height = crop.height;

      // Apply rotation around center
      const rotRad = (rotation * Math.PI) / 180;
      cropCtx.translate(cropCanvas.width / 2, cropCanvas.height / 2);
      cropCtx.rotate(rotRad);
      cropCtx.translate(-cropCanvas.width / 2, -cropCanvas.height / 2);

      // Draw cropped portion
      cropCtx.drawImage(
        img,
        crop.x,
        crop.y,
        crop.width,
        crop.height,
        0,
        0,
        crop.width,
        crop.height
      );

      // Calculate target dimensions for resizing
      const { width: targetWidth, height: targetHeight } = calculateTargetDimensions(crop.width, crop.height);

      // Create final canvas with target dimensions
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = targetWidth;
      finalCanvas.height = targetHeight;

      const finalCtx = finalCanvas.getContext('2d');
      if (!finalCtx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Draw resized image
      finalCtx.drawImage(cropCanvas, 0, 0, targetWidth, targetHeight);

      finalCanvas.toBlob(
        (blob) => {
          if (blob) {
            resolve({ blob, width: targetWidth, height: targetHeight });
          } else {
            reject(new Error('Failed to create blob'));
          }
        },
        format,
        quality
      );
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageSrc;
  });
}

/**
 * Rotate image by 90 degrees
 */
export async function rotateImage(imageSrc: string, clockwise = true): Promise<string> {
  const format = await getPreferredFormat();
  const quality = getQualityForFormat(format);

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      // Swap dimensions for 90 degree rotation
      canvas.width = img.naturalHeight;
      canvas.height = img.naturalWidth;

      // Rotate
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(((clockwise ? 90 : -90) * Math.PI) / 180);
      ctx.translate(-img.naturalWidth / 2, -img.naturalHeight / 2);
      ctx.drawImage(img, 0, 0);

      resolve(canvas.toDataURL(format, quality));
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageSrc;
  });
}
